import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import SpirisIcon from '../Icon/SpirisIcon';
import { useOutsideClick } from '../_hooks/useOutsideClick';

export interface DropdownMenuItem {
    key: string;
    label: ReactNode;
    onClick: () => void;
    icon?: string;
    active?: boolean;
    destructive?: boolean;
}

interface DropdownMenuProps {
    label: string;
    items: DropdownMenuItem[];
    trigger: ReactNode;
    triggerClassName?: string;
}

// WAI-ARIA menu button: focus moves into the menu when it opens, arrows/Home/End move between items, Escape returns to the trigger
const DropdownMenu = ({ label, items, trigger, triggerClassName = 'btn btn-secondary dropdown-toggle' }: DropdownMenuProps) => {
    const [open, setOpen] = useState(false);
    const [initialFocus, setInitialFocus] = useState<'first' | 'last'>('first');
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const itemsRef = useRef<(HTMLAnchorElement | null)[]>([]);
    const menuId = useId();

    const close = useCallback(() => setOpen(false), []);
    useOutsideClick(open, containerRef, containerRef, close);

    const menuItems = () => itemsRef.current.slice(0, items.length).filter((element): element is HTMLAnchorElement => element !== null);

    const focusItem = (index: number) => {
        const elements = menuItems();
        if (elements.length === 0) return;
        elements[((index % elements.length) + elements.length) % elements.length]?.focus();
    };

    useEffect(() => {
        if (open) focusItem(initialFocus === 'first' ? 0 : -1);
        // focusItem reads refs that are only set once the menu has rendered, so this runs on open/close only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialFocus]);

    const openWith = (focus: 'first' | 'last') => {
        setInitialFocus(focus);
        setOpen(true);
    };

    const closeAndRefocus = () => {
        close();
        triggerRef.current?.focus();
    };

    const select = (item: DropdownMenuItem) => {
        closeAndRefocus();
        item.onClick();
    };

    const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            openWith(event.key === 'ArrowDown' ? 'first' : 'last');
        }
    };

    const onMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
        const current = menuItems().findIndex((element) => element === document.activeElement);

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                focusItem(current + 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                focusItem(current - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusItem(0);
                break;
            case 'End':
                event.preventDefault();
                focusItem(-1);
                break;
            case 'Tab':
                close();
                break;
            default:
        }
    };

    // m-0 rather than mb-0: Spiris's `.dropdown` also carries a right margin, which would add to the gap of any flex row this sits in
    return (
        <div
            className={`dropdown d-inline-block m-0${open ? ' open' : ''}`}
            ref={containerRef}
            // the menu often sits in a table row that has its own click handler
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
                if (event.key === 'Escape' && open) {
                    event.preventDefault();
                    // a Drawer listens for Escape on document; the menu consumed this one
                    event.stopPropagation();
                    closeAndRefocus();
                }
            }}
        >
            <button
                ref={triggerRef}
                type="button"
                className={triggerClassName}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={menuId}
                aria-label={label}
                title={label}
                onClick={() => (open ? close() : openWith('first'))}
                onKeyDown={onTriggerKeyDown}
            >
                {trigger}
            </button>
            {open && (
                <ul id={menuId} className="dropdown-menu right-0 left-auto min-w-max-content" role="menu" aria-label={label} onKeyDown={onMenuKeyDown}>
                    {items.map((item, index) => (
                        <li key={item.key} role="none" className={`dropdown-item${item.active === true ? ' active' : ''}`}>
                            <a
                                ref={(element) => {
                                    itemsRef.current[index] = element;
                                }}
                                role="menuitem"
                                tabIndex={-1}
                                className={`d-flex align-items-center gap-8${item.destructive === true ? ' text-error' : ''}`}
                                onClick={() => select(item)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        select(item);
                                    }
                                }}
                            >
                                {item.icon !== undefined && <SpirisIcon name={item.icon} size="sm" />}
                                {item.label}
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default DropdownMenu;
